<template>
  <!--
    One card per carrier whose payer rejected the practice's BILLING PROVIDER (backend migration
    005, 2026-09-18). Automatic checks for that carrier are paused at this site until an admin
    clicks "Resume checks" — nothing retries on its own. Renders nothing when there are no pauses.
  -->
  <div v-if="views.length" class="iv-pause-list" data-test="carrier-pause-banner">
    <div v-for="v in views" :key="v.key" class="iv-pause-card" :data-test="`carrier-pause-${v.carrierNum}`">
      <span class="iv-pause-icon"><i class="fa fa-pause-circle" aria-hidden="true"></i></span>

      <div class="iv-pause-body">
        <div class="iv-pause-title">{{ v.title }}</div>
        <div class="iv-pause-reason">
          {{ v.reason }}<template v-if="v.since"> Since {{ v.since }}.</template>
        </div>
        <div class="iv-pause-heldback">{{ v.heldBack }}</div>
        <div v-if="v.provider" class="iv-pause-provider">Billing provider: {{ v.provider }}</div>

        <div class="iv-pause-steps-title">What to do</div>
        <ol class="iv-pause-steps">
          <li v-for="(s, i) in v.steps" :key="i">{{ s }}</li>
        </ol>

        <!--
          ⛔ Resume RESTARTS SPEND on this carrier, so it is admin-only (the Precheck-settings
          convention — the page passes isAdminOrAbove) and always goes through a confirm that says
          so. A second click never sends a second request: the button disables while resuming.
        -->
        <template v-if="canResume">
          <div v-if="confirmingCarrierNum === v.carrierNum" class="iv-pause-confirm" data-test="carrier-pause-confirm">
            <div class="iv-pause-confirm-text">{{ v.confirm }}</div>
            <div class="iv-pause-confirm-actions">
              <button type="button" class="iv-pause-btn iv-pause-btn--ghost" @click="confirmingCarrierNum = null">
                Cancel
              </button>
              <button
                type="button"
                class="iv-pause-btn iv-pause-btn--primary"
                data-test="carrier-pause-confirm-resume"
                :disabled="resumingCarrierNum === v.carrierNum"
                @click="confirmResume(v.carrierNum)"
              >
                <i v-if="resumingCarrierNum === v.carrierNum" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                {{ resumingCarrierNum === v.carrierNum ? 'Resuming…' : 'Resume checks' }}
              </button>
            </div>
          </div>
          <button
            v-else
            type="button"
            class="iv-pause-btn iv-pause-btn--primary"
            data-test="carrier-pause-resume"
            :disabled="resumingCarrierNum === v.carrierNum"
            @click="confirmingCarrierNum = v.carrierNum"
          >
            Resume checks
          </button>
        </template>
        <div v-else class="iv-pause-admin-note">An admin can resume checks once this is fixed.</div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { carrierPauseView } from './constants.js';

export default {
  name: 'CarrierPauseBanner',
  props: {
    // The dashboard payload's `carrierPauses[]` (backend loadCarrierPauses).
    pauses: { type: Array, default: () => [] },
    // Admin/Super-Admin only — resuming restarts billed checks on the carrier.
    canResume: { type: Boolean, default: false },
    // The carrier whose resume request is in flight (the page owns the request).
    resumingCarrierNum: { type: [Number, String], default: null },
    // Site timezone for the "Since …" date.
    timezone: { type: String, default: '' },
  },
  data() {
    return { confirmingCarrierNum: null };
  },
  computed: {
    views() {
      return (Array.isArray(this.pauses) ? this.pauses : []).map((p) => carrierPauseView(p, this.timezone));
    },
  },
  watch: {
    // The banner card disappears once the refetch no longer lists it — drop a stale confirm.
    pauses() {
      if (!this.views.some((v) => v.carrierNum === this.confirmingCarrierNum)) this.confirmingCarrierNum = null;
    },
  },
  methods: {
    confirmResume(carrierNum) {
      if (!this.canResume || this.resumingCarrierNum === carrierNum) return;
      this.$emit('resume', carrierNum);
    },
  },
};
</script>

<style lang="scss" scoped>
.iv-pause-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 12px 0;
}

.iv-pause-card {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #fef6e7;
  border: 1px solid #f5d99b;
  border-radius: 10px;
}

.iv-pause-icon {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: #f59e0b;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.iv-pause-body {
  min-width: 0;
  flex: 1;
}

.iv-pause-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #92400e;
}

.iv-pause-reason,
.iv-pause-heldback,
.iv-pause-provider {
  margin-top: 4px;
  font-size: 0.8125rem;
  color: #78350f;
  line-height: 1.45;
  word-break: break-word;
}

.iv-pause-steps-title {
  margin-top: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #92400e;
}

.iv-pause-steps {
  margin: 4px 0 10px;
  padding-left: 18px;
  font-size: 0.8125rem;
  color: #78350f;
  line-height: 1.5;
}

.iv-pause-admin-note {
  font-size: 0.75rem;
  color: #92400e;
  font-style: italic;
}

.iv-pause-confirm {
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #f5d99b;
  border-radius: 8px;
}

.iv-pause-confirm-text {
  font-size: 0.8125rem;
  color: #334155;
  line-height: 1.5;
}

.iv-pause-confirm-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.iv-pause-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;

  &--primary {
    border: 1px solid #0f7259;
    background: #0f7259;
    color: #ffffff;

    &:not(:disabled):hover {
      background: #0b5c49;
    }
  }

  &--ghost {
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #0b5c49;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

@media (max-width: 600px) {
  .iv-pause-card {
    flex-direction: column;
  }
}
</style>
